/**
 * Reporter — Main container.
 *
 * Compliance-only. No internal sidebar; Core's AppSidebar handles app navigation.
 * Visual cadence follows PPM: header strip with horizontal tab navigation.
 *
 * Tabs:
 *   - Sections        : per-section workspace (upload universe + .txt, see report)
 *   - Runs            : recent validation runs
 *   - Audit Trail     : append-only event log
 *   - Specs           : CMS PDFs in the intake area (Spec Reader hooks land in Area 6)
 */

import React, { useState, useEffect } from 'react';
import ReporterHome from './ReporterHome';
import ReporterSectionWork from './ReporterSectionWork';
import CollectionPanel from './CollectionPanel';

const API = process.env.REACT_APP_API_URL;

const TABS = [
  { id: 'sections',    label: 'Sections',    icon: '§' },
  // Collection is spliced in after Sections only when the backend has it on.
  { id: 'submissions', label: 'Submissions', icon: '⎗' },
  { id: 'runs',        label: 'Runs',        icon: '◷' },
  { id: 'audit',       label: 'Audit',       icon: '◇' },
  { id: 'specs',       label: 'Specs',       icon: '⊕' },
];

export default function Reporter() {
  const [view, setView] = useState('sections');
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [sections, setSections] = useState([]);
  const [runs, setRuns] = useState([]);
  const [audit, setAudit] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pendingSpecs, setPendingSpecs] = useState([]);
  const [pendingRulesets, setPendingRulesets] = useState([]);
  // Element-level collection is behind a server flag. Ask once; if the route is
  // not there the tab never appears, so nothing about the existing flow changes.
  const [hasCollection, setHasCollection] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/reporter/elements/catalogue`, { credentials: 'include' })
      .then(r => setHasCollection(r.ok))
      .catch(() => setHasCollection(false));
  }, []);

  useEffect(() => {
    fetch(`${API}/api/reporter/sections`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setSections(d.sections || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (view !== 'runs') return;
    fetch(`${API}/api/reporter/runs`, { credentials: 'include' })
      .then(r => r.json()).then(d => setRuns(d.runs || []))
      .catch(() => {});
  }, [view]);

  useEffect(() => {
    if (view !== 'audit') return;
    fetch(`${API}/api/reporter/audit?limit=200`, { credentials: 'include' })
      .then(r => r.json()).then(d => setAudit(d.events || []))
      .catch(() => {});
  }, [view]);

  function loadSubmissions() {
    fetch(`${API}/api/reporter/submissions`, { credentials: 'include' })
      .then(r => r.json()).then(d => setSubmissions(d.submissions || []))
      .catch(() => {});
  }
  useEffect(() => {
    if (view !== 'submissions') return;
    loadSubmissions();
  }, [view]);

  useEffect(() => {
    if (view !== 'specs') return;
    fetch(`${API}/api/reporter/specs/pending`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPendingSpecs(d.pending || [])).catch(() => {});
    fetch(`${API}/api/reporter/rulesets/pending`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPendingRulesets(d.pending || [])).catch(() => {});
  }, [view]);

  function openSection(sectionId, mode) {
    setSelectedSection(sectionId);
    setSelectedMode(mode || 'preflight');
    setView('section-work');
  }

  const isDetailView = view === 'section-work';

  const visibleTabs = hasCollection
    ? [TABS[0], { id: 'collection', label: 'Collection', icon: '⊞' }, ...TABS.slice(1)]
    : TABS;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <div className="text-xs font-mono uppercase tracking-widest text-red-700">PCG · 14</div>
            <h1 className="text-xl font-semibold text-slate-900">Reporter</h1>
            <div className="text-xs text-slate-500 font-mono">Part C/D Reporting Requirements</div>
          </div>
        </div>
        {!isDetailView && (
          <div className="max-w-7xl mx-auto px-6 flex gap-1">
            {visibleTabs.map(t => (
              <button
                key={t.id}
                onClick={() => { setView(t.id); setSelectedSection(null); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  view === t.id
                    ? 'border-red-700 text-red-700'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="mr-2 font-mono text-xs">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {view === 'sections' && <ReporterHome sections={sections} onOpen={openSection} />}
        {view === 'section-work' && (
          <ReporterSectionWork
            sectionId={selectedSection}
            sectionMode={selectedMode}
            onBack={() => setView('sections')}
          />
        )}
        {view === 'collection' && hasCollection && <CollectionPanel />}
        {view === 'submissions' && <SubmissionsPanel submissions={submissions} onChange={loadSubmissions} />}
        {view === 'runs' && <RunsList runs={runs} />}
        {view === 'audit' && <AuditList events={audit} />}
        {view === 'specs' && <SpecsPanel pendingSpecs={pendingSpecs} pendingRulesets={pendingRulesets} />}
      </div>
    </div>
  );
}

// Submissions register: what was submitted as final, when, by whom, whether
// errors existed, and where it stands. A compliance reviewer (someone other
// than the submitter) can accept a submission or send it back for correction.
function SubmissionsPanel({ submissions, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState('');

  async function review(id, action, rejectReason) {
    setBusyId(id); setError(null);
    try {
      const res = await fetch(`${API}/api/reporter/submissions/${id}/review`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reject_reason: rejectReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Review failed (${res.status}).`);
      setRejectingId(null); setReason('');
      onChange && onChange();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  }

  const STATUS = {
    submitted: { label: 'Awaiting review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    accepted:  { label: 'Accepted',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    rejected:  { label: 'Sent back',       cls: 'bg-red-50 text-red-700 border-red-200' },
  };

  if (!submissions.length) {
    return <div className="text-sm text-slate-500">No submissions yet. Read your counts, reconcile against your records, then use “Submit as final” to record one here.</div>;
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Submissions</h2>
        <p className="text-sm text-slate-600 max-w-2xl mt-1">
          Every final submission is recorded here with its date, period, and whether errors existed. A reviewer other than
          the person who submitted it can accept it, or reject it back for correction with a reason.
        </p>
      </div>
      {error && <div className="mb-3 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>}
      <div className="space-y-2">
        {submissions.map(s => {
          const st = STATUS[s.status] || STATUS.submitted;
          return (
            <div key={s.id} className="border border-slate-200 bg-white px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{s.measure_label || s.measure_id}</span>
                    <span className="text-xs font-mono text-slate-500">{s.measure_id}</span>
                    <span className={`text-[11px] px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
                    {s.had_errors
                      ? <span className="text-[11px] px-2 py-0.5 border bg-amber-50 text-amber-700 border-amber-200">{s.error_count} discrepanc{s.error_count === 1 ? 'y' : 'ies'}</span>
                      : <span className="text-[11px] px-2 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200">clean</span>}
                  </div>
                  <div className="text-[12px] text-slate-600 mt-1">
                    {s.period_label || `CY${s.reporting_year || ''}`} · submitted {new Date(s.submitted_at).toLocaleString()}
                    {s.submitted_by_email ? ` by ${s.submitted_by_email}` : ''}
                  </div>
                  {s.status !== 'submitted' && (
                    <div className="text-[12px] text-slate-500 mt-1">
                      {s.status === 'accepted' ? 'Accepted' : 'Sent back'} {s.reviewed_at ? new Date(s.reviewed_at).toLocaleString() : ''}
                      {s.reviewed_by_email ? ` by ${s.reviewed_by_email}` : ''}
                      {s.status === 'rejected' && s.reject_reason ? ` — “${s.reject_reason}”` : ''}
                    </div>
                  )}
                </div>
                {s.status === 'submitted' && (
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <button onClick={() => review(s.id, 'accept')} disabled={busyId === s.id}
                      className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold">
                      Accept
                    </button>
                    <button onClick={() => { setRejectingId(rejectingId === s.id ? null : s.id); setReason(''); }} disabled={busyId === s.id}
                      className="border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold">
                      Reject
                    </button>
                  </div>
                )}
              </div>
              {rejectingId === s.id && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Reason to send back</label>
                  <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                    placeholder="What needs correcting before this can be filed?"
                    className="w-full border border-slate-300 px-2 py-1 text-sm" />
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => review(s.id, 'reject', reason)} disabled={busyId === s.id || !reason.trim()}
                      className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-3 py-1.5 text-xs font-semibold">
                      Send back for correction
                    </button>
                    <button onClick={() => { setRejectingId(null); setReason(''); }} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RunsList({ runs }) {
  if (runs.length === 0) {
    return <div className="text-sm text-slate-500">No runs yet. Drop a universe and a .txt under a section to validate.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
        <tr>
          <th className="text-left py-2">Run</th>
          <th className="text-left py-2">Section</th>
          <th className="text-left py-2">Contract</th>
          <th className="text-left py-2">Period</th>
          <th className="text-right py-2">Findings</th>
          <th className="text-left py-2 pl-4">When</th>
        </tr>
      </thead>
      <tbody>
        {runs.map(r => (
          <tr key={r.runId} className="border-b border-slate-100">
            <td className="py-2 font-mono text-xs text-slate-700">{r.runId}</td>
            <td className="py-2">{r.section}</td>
            <td className="py-2 font-mono">{r.contract || '—'}</td>
            <td className="py-2 text-xs">Q{r.reportingPeriod?.quarter} {r.reportingPeriod?.year}</td>
            <td className={`py-2 text-right font-mono ${r.findings > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{r.findings}</td>
            <td className="py-2 pl-4 text-xs text-slate-500">{new Date(r.storedAt).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditList({ events }) {
  if (events.length === 0) {
    return <div className="text-sm text-slate-500">No audit events yet.</div>;
  }
  return (
    <div className="space-y-1">
      {events.map(e => (
        <div key={e.id} className="border border-slate-200 bg-white px-3 py-2 text-xs font-mono flex gap-3">
          <span className="text-slate-400 w-40">{new Date(e.created_at).toLocaleString()}</span>
          <span className={`w-44 ${e.outcome === 'failure' ? 'text-red-700' : 'text-slate-800'}`}>{e.event_type}</span>
          <span className="w-12 text-slate-500">{e.section_id || ''}</span>
          <span className="w-8 text-slate-500">{e.element_id || ''}</span>
          <span className="flex-1 text-slate-700 truncate" title={e.citation_quoted || ''}>{e.citation_quoted || ''}</span>
          <span className="text-slate-400 truncate w-48" title={e.actor_email || ''}>{e.actor_email || ''}</span>
        </div>
      ))}
    </div>
  );
}

function SpecsPanel({ pendingSpecs, pendingRulesets }) {
  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">CMS PDFs awaiting Spec Reader review</h2>
        <div className="text-xs text-slate-500 mb-3">
          Drop new CY's CMS PDFs into <code className="font-mono">compliance-docs/reporter/specs/pending-review/</code>.
          The Spec Reader agent (Area 6) will diff and propose YAML ruleset changes.
        </div>
        {pendingSpecs.length === 0 ? (
          <div className="text-sm text-slate-500">Nothing pending.</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {pendingSpecs.map((s, i) => (
              <li key={i} className="font-mono text-xs">{s.filename}</li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">Ruleset diffs awaiting Compliance approval</h2>
        {pendingRulesets.length === 0 ? (
          <div className="text-sm text-slate-500">Nothing pending.</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {pendingRulesets.map((r, i) => (
              <li key={i} className="font-mono text-xs">{r.path}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
